import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApiResponse, BearerKey } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPut, apiPost, apiDelete } from '@/utils/fetchInterceptor';

// Define types for the settings data
interface RoutingConfig {
  enableGlobalRoute: boolean;
  enableGroupNameRoute: boolean;
  enableBearerAuth: boolean;
  bearerAuthKey: string;
  bearerAuthHeaderName: string;
  jsonBodyLimit: string;
  skipAuth: boolean;
}

interface InstallConfig {
  pythonIndexUrl: string;
  npmRegistry: string;
  baseUrl: string;
}

interface SmartRoutingConfig {
  enabled: boolean;
  dbUrl: string;
  basePacingDelayMs?: number;
  embeddingProvider?: 'openai' | 'azure_openai';
  embeddingEncodingFormat?: 'auto' | 'base64' | 'float';
  openaiApiBaseUrl: string;
  openaiApiKey: string;
  openaiApiEmbeddingModel: string;
  azureOpenaiEndpoint?: string;
  azureOpenaiApiKey?: string;
  azureOpenaiApiVersion?: string;
  azureOpenaiEmbeddingDeployment?: string;
  azureOpenaiEmbeddingModel?: string;
  progressiveDisclosure: boolean;
  embeddingMaxTokens?: number;
}

type ToolResultCompressionStrategy = 'auto' | 'json' | 'log' | 'search' | 'diff' | 'text';

interface ToolResultCompressionConfig {
  enabled: boolean;
  minTokens: number;
  maxOutputTokens: number;
  strategy: ToolResultCompressionStrategy;
}

interface MCPRouterConfig {
  apiKey: string;
  referer: string;
  title: string;
  baseUrl: string;
}

interface OAuthServerConfig {
  enabled: boolean;
  accessTokenLifetime: number;
  refreshTokenLifetime: number;
  authorizationCodeLifetime: number;
  requireClientSecret: boolean;
  allowedScopes: string[];
  requireState: boolean;
  dynamicRegistration: {
    enabled: boolean;
    allowedGrantTypes: string[];
    requiresAuthentication: boolean;
  };
}

interface BetterAuthProviderToggle {
  enabled: boolean;
}

interface BetterAuthOidcConfig {
  enabled: boolean;
  providerId: string;
  discoveryUrl: string;
  scopes: string[];
  pkce: boolean;
  prompt: string;
}

interface BetterAuthConfig {
  enabled: boolean;
  basePath: string;
  trustedOrigins: string[];
  providers: {
    google: BetterAuthProviderToggle;
    github: BetterAuthProviderToggle;
    oidc: BetterAuthOidcConfig;
  };
}

interface ModelscopeConfig {
  apiKey: string;
}

interface EmailConfig {
  enabled: boolean;
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromName: string;
  fromEmail: string;
  baseUrl: string;
}

interface SystemSettings {
  systemConfig?: {
    routing?: RoutingConfig;
    install?: InstallConfig;
    smartRouting?: SmartRoutingConfig;
    toolResultCompression?: ToolResultCompressionConfig;
    mcpRouter?: MCPRouterConfig;
    modelscope?: ModelscopeConfig;
    email?: Partial<EmailConfig>;
    nameSeparator?: string;
    oauthServer?: OAuthServerConfig;
    auth?: {
      betterAuth?: Partial<BetterAuthConfig>;
    };
    enableSessionRebuild?: boolean;
    activityLog?: {
      storeToolPayload?: boolean;
    };
  };
  bearerKeys?: BearerKey[];
}

interface TempRoutingConfig {
  bearerAuthKey: string;
  bearerAuthHeaderName: string;
  jsonBodyLimit: string;
}

interface SettingsContextValue {
  routingConfig: RoutingConfig;
  tempRoutingConfig: TempRoutingConfig;
  setTempRoutingConfig: React.Dispatch<React.SetStateAction<TempRoutingConfig>>;
  installConfig: InstallConfig;
  smartRoutingConfig: SmartRoutingConfig;
  toolResultCompressionConfig: ToolResultCompressionConfig;
  mcpRouterConfig: MCPRouterConfig;
  modelscopeConfig: ModelscopeConfig;
  emailConfig: EmailConfig;
  oauthServerConfig: OAuthServerConfig;
  betterAuthConfig: BetterAuthConfig;
  nameSeparator: string;
  enableSessionRebuild: boolean;
  storeToolPayload: boolean;
  bearerKeys: BearerKey[];
  loading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  triggerRefresh: () => void;
  fetchSettings: () => Promise<void>;
  updateRoutingConfig: (key: keyof RoutingConfig, value: any) => Promise<boolean | undefined>;
  updateInstallConfig: (key: keyof InstallConfig, value: any) => Promise<boolean | undefined>;
  updateSmartRoutingConfig: (
    key: keyof SmartRoutingConfig,
    value: any,
  ) => Promise<boolean | undefined>;
  updateSmartRoutingConfigBatch: (
    updates: Partial<SmartRoutingConfig>,
  ) => Promise<boolean | undefined>;
  updateModelscopeConfig: (apiKey: string) => Promise<boolean | undefined>;
  updateEmailConfigBatch: (updates: Partial<EmailConfig>) => Promise<boolean | undefined>;
  updateToolResultCompressionConfig: (
    key: keyof ToolResultCompressionConfig,
    value: any,
  ) => Promise<boolean | undefined>;
  updateToolResultCompressionConfigBatch: (
    updates: Partial<ToolResultCompressionConfig>,
  ) => Promise<boolean | undefined>;
  updateRoutingConfigBatch: (updates: Partial<RoutingConfig>) => Promise<boolean | undefined>;
  updateMCPRouterConfig: (key: keyof MCPRouterConfig, value: any) => Promise<boolean | undefined>;
  updateMCPRouterConfigBatch: (updates: Partial<MCPRouterConfig>) => Promise<boolean | undefined>;
  updateOAuthServerConfig: (
    key: keyof OAuthServerConfig,
    value: any,
  ) => Promise<boolean | undefined>;
  updateOAuthServerConfigBatch: (
    updates: Partial<OAuthServerConfig>,
  ) => Promise<boolean | undefined>;
  updateBetterAuthConfigBatch: (
    updates: Partial<BetterAuthConfig>,
  ) => Promise<boolean | undefined>;
  updateNameSeparator: (value: string) => Promise<boolean | undefined>;
  updateSessionRebuild: (value: boolean) => Promise<boolean | undefined>;
  updateStoreToolPayload: (value: boolean) => Promise<boolean | undefined>;
  exportMCPSettings: (serverName?: string) => Promise<any>;
  // Bearer key management
  refreshBearerKeys: () => Promise<void>;
  createBearerKey: (
    payload: Pick<BearerKey, 'name'> & Partial<Omit<BearerKey, 'id' | 'name' | 'token'>>,
  ) => Promise<BearerKey | null>;
  updateBearerKey: (
    id: string,
    updates: Partial<Omit<BearerKey, 'id'>>,
  ) => Promise<BearerKey | null>;
  deleteBearerKey: (id: string) => Promise<boolean>;
}

const getDefaultOAuthServerConfig = (): OAuthServerConfig => ({
  enabled: true,
  accessTokenLifetime: 3600,
  refreshTokenLifetime: 1209600,
  authorizationCodeLifetime: 300,
  requireClientSecret: false,
  allowedScopes: ['read', 'write'],
  requireState: false,
  dynamicRegistration: {
    enabled: true,
    allowedGrantTypes: ['authorization_code', 'refresh_token'],
    requiresAuthentication: false,
  },
});

const DEFAULT_OIDC_SCOPES = ['openid', 'profile', 'email'];

const getDefaultBetterAuthConfig = (): BetterAuthConfig => ({
  enabled: true,
  basePath: '/api/auth/better',
  trustedOrigins: [],
  providers: {
    google: {
      enabled: true,
    },
    github: {
      enabled: true,
    },
    oidc: {
      enabled: false,
      providerId: 'oidc',
      discoveryUrl: '',
      scopes: [...DEFAULT_OIDC_SCOPES],
      pkce: true,
      prompt: '',
    },
  },
});

const normalizeStringArray = (value: unknown, fallback: string[] = []): string[] => {
  const normalized = Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];

  return normalized.length > 0 ? normalized : [...fallback];
};

const normalizeBetterAuthConfig = (
  config?: Partial<BetterAuthConfig>,
): BetterAuthConfig => {
  const defaults = getDefaultBetterAuthConfig();

  return {
    enabled: config?.enabled ?? defaults.enabled,
    basePath: config?.basePath?.trim() || defaults.basePath,
    trustedOrigins: normalizeStringArray(config?.trustedOrigins, defaults.trustedOrigins),
    providers: {
      google: {
        enabled: config?.providers?.google?.enabled ?? defaults.providers.google.enabled,
      },
      github: {
        enabled: config?.providers?.github?.enabled ?? defaults.providers.github.enabled,
      },
      oidc: {
        enabled: config?.providers?.oidc?.enabled ?? defaults.providers.oidc.enabled,
        providerId: config?.providers?.oidc?.providerId?.trim() || defaults.providers.oidc.providerId,
        discoveryUrl: config?.providers?.oidc?.discoveryUrl?.trim() || '',
        scopes: normalizeStringArray(config?.providers?.oidc?.scopes, DEFAULT_OIDC_SCOPES),
        pkce: config?.providers?.oidc?.pkce ?? defaults.providers.oidc.pkce,
        prompt: config?.providers?.oidc?.prompt?.trim() || '',
      },
    },
  };
};

const mergeBetterAuthConfig = (
  current: BetterAuthConfig,
  updates: Partial<BetterAuthConfig>,
): BetterAuthConfig => {
  const nextConfig: Partial<BetterAuthConfig> = {
    ...current,
    ...updates,
    trustedOrigins: updates.trustedOrigins ?? current.trustedOrigins,
    providers: {
      ...current.providers,
      ...updates.providers,
      google: {
        ...current.providers.google,
        ...updates.providers?.google,
      },
      github: {
        ...current.providers.github,
        ...updates.providers?.github,
      },
      oidc: {
        ...current.providers.oidc,
        ...updates.providers?.oidc,
        scopes: updates.providers?.oidc?.scopes ?? current.providers.oidc.scopes,
      },
    },
  };

  return normalizeBetterAuthConfig(nextConfig);
};

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { auth } = useAuth();

  const [routingConfig, setRoutingConfig] = useState<RoutingConfig>({
    enableGlobalRoute: true,
    enableGroupNameRoute: true,
    enableBearerAuth: true,
    bearerAuthKey: '',
    bearerAuthHeaderName: 'Authorization',
    jsonBodyLimit: '1mb',
    skipAuth: false,
  });

  const [tempRoutingConfig, setTempRoutingConfig] = useState<TempRoutingConfig>({
    bearerAuthKey: '',
    bearerAuthHeaderName: 'Authorization',
    jsonBodyLimit: '1mb',
  });

  const [installConfig, setInstallConfig] = useState<InstallConfig>({
    pythonIndexUrl: '',
    npmRegistry: '',
    baseUrl: 'http://localhost:3000',
  });

  const [smartRoutingConfig, setSmartRoutingConfig] = useState<SmartRoutingConfig>({
    enabled: false,
    dbUrl: '',
    basePacingDelayMs: undefined,
    embeddingProvider: 'openai',
    embeddingEncodingFormat: 'auto',
    openaiApiBaseUrl: '',
    openaiApiKey: '',
    openaiApiEmbeddingModel: '',
    azureOpenaiEndpoint: '',
    azureOpenaiApiKey: '',
    azureOpenaiApiVersion: '',
    azureOpenaiEmbeddingDeployment: '',
    azureOpenaiEmbeddingModel: '',
    progressiveDisclosure: false,
    embeddingMaxTokens: undefined,
  });

  const [toolResultCompressionConfig, setToolResultCompressionConfig] =
    useState<ToolResultCompressionConfig>({
      enabled: false,
      minTokens: 2000,
      maxOutputTokens: 1200,
      strategy: 'auto',
    });

  const [mcpRouterConfig, setMCPRouterConfig] = useState<MCPRouterConfig>({
    apiKey: '',
    referer: 'https://github.com/huangjunsen0406/xiaozhi-mcphub',
    title: 'MCPHub',
    baseUrl: 'https://api.mcprouter.to/v1',
  });

  const [modelscopeConfig, setModelscopeConfig] = useState<ModelscopeConfig>({
    apiKey: '',
  });

  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    enabled: false,
    host: '',
    port: 465,
    secure: true,
    user: '',
    password: '',
    fromName: '',
    fromEmail: '',
    baseUrl: '',
  });

  const [oauthServerConfig, setOAuthServerConfig] = useState<OAuthServerConfig>(
    getDefaultOAuthServerConfig(),
  );
  const [betterAuthConfig, setBetterAuthConfig] = useState<BetterAuthConfig>(
    getDefaultBetterAuthConfig(),
  );

  const [nameSeparator, setNameSeparator] = useState<string>('-');
  const [enableSessionRebuild, setEnableSessionRebuild] = useState<boolean>(false);
  const [storeToolPayload, setStoreToolPayload] = useState<boolean>(true);
  const [bearerKeys, setBearerKeys] = useState<BearerKey[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Trigger a refresh of the settings data
  const triggerRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Fetch current settings
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data: ApiResponse<SystemSettings> = await apiGet('/settings');

      if (data.success && data.data?.systemConfig?.routing) {
        setRoutingConfig({
          enableGlobalRoute: data.data.systemConfig.routing.enableGlobalRoute ?? true,
          enableGroupNameRoute: data.data.systemConfig.routing.enableGroupNameRoute ?? true,
          enableBearerAuth: data.data.systemConfig.routing.enableBearerAuth ?? true,
          bearerAuthKey: data.data.systemConfig.routing.bearerAuthKey || '',
          bearerAuthHeaderName:
            data.data.systemConfig.routing.bearerAuthHeaderName || 'Authorization',
          jsonBodyLimit: data.data.systemConfig.routing.jsonBodyLimit || '1mb',
          skipAuth: data.data.systemConfig.routing.skipAuth ?? false,
        });
      }
      if (data.success && data.data?.systemConfig?.install) {
        setInstallConfig({
          pythonIndexUrl: data.data.systemConfig.install.pythonIndexUrl || '',
          npmRegistry: data.data.systemConfig.install.npmRegistry || '',
          baseUrl: data.data.systemConfig.install.baseUrl || 'http://localhost:3000',
        });
      }
      if (data.success && data.data?.systemConfig?.smartRouting) {
        setSmartRoutingConfig({
          enabled: data.data.systemConfig.smartRouting.enabled ?? false,
          dbUrl: data.data.systemConfig.smartRouting.dbUrl || '',
          basePacingDelayMs: data.data.systemConfig.smartRouting.basePacingDelayMs,
          embeddingProvider:
            data.data.systemConfig.smartRouting.embeddingProvider === 'azure_openai'
              ? 'azure_openai'
              : 'openai',
          embeddingEncodingFormat:
            data.data.systemConfig.smartRouting.embeddingEncodingFormat === 'base64'
              ? 'base64'
              : data.data.systemConfig.smartRouting.embeddingEncodingFormat === 'float'
                ? 'float'
                : 'auto',
          openaiApiBaseUrl: data.data.systemConfig.smartRouting.openaiApiBaseUrl || '',
          openaiApiKey: data.data.systemConfig.smartRouting.openaiApiKey || '',
          openaiApiEmbeddingModel:
            data.data.systemConfig.smartRouting.openaiApiEmbeddingModel || '',
          azureOpenaiEndpoint: data.data.systemConfig.smartRouting.azureOpenaiEndpoint || '',
          azureOpenaiApiKey: data.data.systemConfig.smartRouting.azureOpenaiApiKey || '',
          azureOpenaiApiVersion: data.data.systemConfig.smartRouting.azureOpenaiApiVersion || '',
          azureOpenaiEmbeddingDeployment:
            data.data.systemConfig.smartRouting.azureOpenaiEmbeddingDeployment || '',
          azureOpenaiEmbeddingModel:
            data.data.systemConfig.smartRouting.azureOpenaiEmbeddingModel || '',
          progressiveDisclosure: data.data.systemConfig.smartRouting.progressiveDisclosure ?? false,
          embeddingMaxTokens: data.data.systemConfig.smartRouting.embeddingMaxTokens,
        });
      }
      if (data.success && data.data?.systemConfig?.toolResultCompression) {
        const toolResultCompression = data.data.systemConfig.toolResultCompression;
        const strategy = toolResultCompression.strategy;
        setToolResultCompressionConfig({
          enabled: toolResultCompression.enabled ?? false,
          minTokens: toolResultCompression.minTokens || 2000,
          maxOutputTokens: toolResultCompression.maxOutputTokens || 1200,
          strategy: ['auto', 'json', 'log', 'search', 'diff', 'text'].includes(strategy)
            ? strategy
            : 'auto',
        });
      }
      if (data.success && data.data?.systemConfig?.mcpRouter) {
        setMCPRouterConfig({
          apiKey: data.data.systemConfig.mcpRouter.apiKey || '',
          referer: data.data.systemConfig.mcpRouter.referer || 'https://github.com/huangjunsen0406/xiaozhi-mcphub',
          title: data.data.systemConfig.mcpRouter.title || 'MCPHub',
          baseUrl: data.data.systemConfig.mcpRouter.baseUrl || 'https://api.mcprouter.to/v1',
        });
      }
      if (data.success && data.data?.systemConfig?.modelscope) {
        setModelscopeConfig({ apiKey: data.data.systemConfig.modelscope.apiKey || '' });
      }
      if (data.success && data.data?.systemConfig?.email) {
        const emailCfg = data.data.systemConfig.email;
        setEmailConfig({
          enabled: emailCfg.enabled ?? false,
          host: emailCfg.host || '',
          port: emailCfg.port ?? 465,
          secure: emailCfg.secure ?? true,
          user: emailCfg.user || '',
          password: emailCfg.password || '',
          fromName: emailCfg.fromName || '',
          fromEmail: emailCfg.fromEmail || '',
          baseUrl: emailCfg.baseUrl || '',
        });
      }
      if (data.success) {
        if (data.data?.systemConfig?.oauthServer) {
          const oauth = data.data.systemConfig.oauthServer;
          const defaultOauthConfig = getDefaultOAuthServerConfig();
          const defaultDynamic = defaultOauthConfig.dynamicRegistration;
          const allowedScopes = Array.isArray(oauth.allowedScopes)
            ? [...oauth.allowedScopes]
            : [...defaultOauthConfig.allowedScopes];
          const dynamicAllowedGrantTypes = Array.isArray(
            oauth.dynamicRegistration?.allowedGrantTypes,
          )
            ? [...oauth.dynamicRegistration!.allowedGrantTypes!]
            : [...defaultDynamic.allowedGrantTypes];

          setOAuthServerConfig({
            enabled: oauth.enabled ?? defaultOauthConfig.enabled,
            accessTokenLifetime:
              oauth.accessTokenLifetime ?? defaultOauthConfig.accessTokenLifetime,
            refreshTokenLifetime:
              oauth.refreshTokenLifetime ?? defaultOauthConfig.refreshTokenLifetime,
            authorizationCodeLifetime:
              oauth.authorizationCodeLifetime ?? defaultOauthConfig.authorizationCodeLifetime,
            requireClientSecret:
              oauth.requireClientSecret ?? defaultOauthConfig.requireClientSecret,
            requireState: oauth.requireState ?? defaultOauthConfig.requireState,
            allowedScopes,
            dynamicRegistration: {
              enabled: oauth.dynamicRegistration?.enabled ?? defaultDynamic.enabled,
              allowedGrantTypes: dynamicAllowedGrantTypes,
              requiresAuthentication:
                oauth.dynamicRegistration?.requiresAuthentication ??
                defaultDynamic.requiresAuthentication,
            },
          });
        } else {
          setOAuthServerConfig(getDefaultOAuthServerConfig());
        }

        setBetterAuthConfig(
          normalizeBetterAuthConfig(data.data?.systemConfig?.auth?.betterAuth),
        );
      }
      if (data.success && data.data?.systemConfig?.nameSeparator !== undefined) {
        setNameSeparator(data.data.systemConfig.nameSeparator);
      }
      if (data.success && data.data?.systemConfig?.enableSessionRebuild !== undefined) {
        setEnableSessionRebuild(data.data.systemConfig.enableSessionRebuild);
      }
      if (data.success && data.data?.systemConfig?.activityLog?.storeToolPayload !== undefined) {
        setStoreToolPayload(data.data.systemConfig.activityLog.storeToolPayload);
      }

      if (data.success && Array.isArray(data.data?.bearerKeys)) {
        setBearerKeys(data.data.bearerKeys);
      }
    } catch (error) {
      console.error('Failed to fetch settings', { error });
      setError(error instanceof Error ? error.message : 'Failed to fetch settings');
      showToast(t('errors.failedToFetchSettings'));
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  // Update routing configuration
  const updateRoutingConfig = async (key: keyof RoutingConfig, value: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        routing: {
          [key]: value,
        },
      });

      if (data.success) {
        setRoutingConfig({
          ...routingConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update routing config');
        showToast(data.error || t('errors.failedToUpdateRoutingConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update routing config', { key, value, error });
      setError(error instanceof Error ? error.message : 'Failed to update routing config');
      showToast(t('errors.failedToUpdateRoutingConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update install configuration
  const updateInstallConfig = async (key: keyof InstallConfig, value: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        install: {
          [key]: value,
        },
      });

      if (data.success) {
        setInstallConfig({
          ...installConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update install config');
        showToast(data.error || t('errors.failedToUpdateInstallConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update install config', { key, value, error });
      setError(error instanceof Error ? error.message : 'Failed to update install config');
      showToast(t('errors.failedToUpdateInstallConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update smart routing configuration
  const updateSmartRoutingConfig = async (key: keyof SmartRoutingConfig, value: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        smartRouting: {
          [key]: value,
        },
      });

      if (data.success) {
        setSmartRoutingConfig({
          ...smartRoutingConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update smart routing config');
        showToast(data.error || t('errors.failedToUpdateSmartRoutingConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update smart routing config', { key, value, error });
      setError(error instanceof Error ? error.message : 'Failed to update smart routing config');
      showToast(t('errors.failedToUpdateSmartRoutingConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update smart routing configuration
  const updateSmartRoutingConfigBatch = async (updates: Partial<SmartRoutingConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        smartRouting: updates,
      });

      if (data.success) {
        setSmartRoutingConfig({
          ...smartRoutingConfig,
          ...updates,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update smart routing config');
        showToast(data.error || t('errors.failedToUpdateSmartRoutingConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update smart routing config', { updates, error });
      setError(error instanceof Error ? error.message : 'Failed to update smart routing config');
      showToast(t('errors.failedToUpdateSmartRoutingConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update tool result compression configuration
  const updateToolResultCompressionConfig = async (
    key: keyof ToolResultCompressionConfig,
    value: any,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        toolResultCompression: {
          [key]: value,
        },
      });

      if (data.success) {
        setToolResultCompressionConfig({
          ...toolResultCompressionConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update tool result compression config');
        showToast(data.error || t('errors.failedToUpdateSettings'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update tool result compression config', { key, value, error });
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to update tool result compression config',
      );
      showToast(t('errors.failedToUpdateSettings'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update tool result compression configuration
  const updateToolResultCompressionConfigBatch = async (
    updates: Partial<ToolResultCompressionConfig>,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        toolResultCompression: updates,
      });

      if (data.success) {
        setToolResultCompressionConfig({
          ...toolResultCompressionConfig,
          ...updates,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update tool result compression config');
        showToast(data.error || t('errors.failedToUpdateSettings'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update tool result compression config', {
        updates,
        error,
      });
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to update tool result compression config',
      );
      showToast(t('errors.failedToUpdateSettings'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update routing configuration
  const updateRoutingConfigBatch = async (updates: Partial<RoutingConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        routing: updates,
      });

      if (data.success) {
        setRoutingConfig({
          ...routingConfig,
          ...updates,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update routing config');
        showToast(data.error || t('errors.failedToUpdateRoutingConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update routing config', { updates, error });
      setError(error instanceof Error ? error.message : 'Failed to update routing config');
      showToast(t('errors.failedToUpdateRoutingConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update MCP Router configuration
  // Update ModelScope configuration
  const updateModelscopeConfig = async (apiKey: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        modelscope: { apiKey },
      });

      if (data.success) {
        setModelscopeConfig({ apiKey });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update ModelScope config');
        showToast(data.error || t('errors.failedToUpdateSystemConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update ModelScope config', error);
      setError(error instanceof Error ? error.message : 'Failed to update ModelScope config');
      showToast(t('errors.failedToUpdateSystemConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update Email (SMTP) configuration
  const updateEmailConfigBatch = async (updates: Partial<EmailConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        email: updates,
      });

      if (data.success) {
        setEmailConfig((prev) => ({ ...prev, ...updates }));
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update email config');
        showToast(data.error || t('errors.failedToUpdateSystemConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update email config', error);
      setError(error instanceof Error ? error.message : 'Failed to update email config');
      showToast(t('errors.failedToUpdateSystemConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateMCPRouterConfig = async (key: keyof MCPRouterConfig, value: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        mcpRouter: {
          [key]: value,
        },
      });

      if (data.success) {
        setMCPRouterConfig({
          ...mcpRouterConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update MCP Router config');
        showToast(data.error || t('errors.failedToUpdateMCPRouterConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update MCP Router config', { key, value, error });
      setError(error instanceof Error ? error.message : 'Failed to update MCP Router config');
      showToast(t('errors.failedToUpdateMCPRouterConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update MCP Router configuration
  const updateMCPRouterConfigBatch = async (updates: Partial<MCPRouterConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        mcpRouter: updates,
      });

      if (data.success) {
        setMCPRouterConfig({
          ...mcpRouterConfig,
          ...updates,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update MCP Router config');
        showToast(data.error || t('errors.failedToUpdateMCPRouterConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update MCP Router config', { updates, error });
      setError(error instanceof Error ? error.message : 'Failed to update MCP Router config');
      showToast(t('errors.failedToUpdateMCPRouterConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update OAuth server configuration
  const updateOAuthServerConfig = async (key: keyof OAuthServerConfig, value: any) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        oauthServer: {
          [key]: value,
        },
      });

      if (data.success) {
        setOAuthServerConfig({
          ...oauthServerConfig,
          [key]: value,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update OAuth server config');
        showToast(data.error || t('errors.failedToUpdateOAuthServerConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update OAuth server config', { key, value, error });
      setError(error instanceof Error ? error.message : 'Failed to update OAuth server config');
      showToast(t('errors.failedToUpdateOAuthServerConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update OAuth server configuration
  const updateOAuthServerConfigBatch = async (updates: Partial<OAuthServerConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        oauthServer: updates,
      });

      if (data.success) {
        setOAuthServerConfig({
          ...oauthServerConfig,
          ...updates,
        });
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update OAuth server config');
        showToast(data.error || t('errors.failedToUpdateOAuthServerConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update OAuth server config', { updates, error });
      setError(error instanceof Error ? error.message : 'Failed to update OAuth server config');
      showToast(t('errors.failedToUpdateOAuthServerConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Batch update Better Auth configuration
  const updateBetterAuthConfigBatch = async (updates: Partial<BetterAuthConfig>) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        auth: {
          betterAuth: updates,
        },
      });

      if (data.success) {
        setBetterAuthConfig((current) => mergeBetterAuthConfig(current, updates));
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update Better Auth config');
        showToast(data.error || t('errors.failedToUpdateBetterAuthConfig'));
        return false;
      }
    } catch (error) {
      console.error('Failed to batch update Better Auth config', { updates, error });
      setError(error instanceof Error ? error.message : 'Failed to update Better Auth config');
      showToast(t('errors.failedToUpdateBetterAuthConfig'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update name separator
  const updateNameSeparator = async (value: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        nameSeparator: value,
      });

      if (data.success) {
        setNameSeparator(value);
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update name separator');
        showToast(data.error || t('errors.failedToUpdateNameSeparator'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update name separator', { value, error });
      setError(error instanceof Error ? error.message : 'Failed to update name separator');
      showToast(t('errors.failedToUpdateNameSeparator'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update session rebuild flag
  const updateSessionRebuild = async (value: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        enableSessionRebuild: value,
      });

      if (data.success) {
        setEnableSessionRebuild(value);
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update session rebuild setting');
        showToast(data.error || t('errors.failedToUpdateSessionRebuild'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update session rebuild setting', { value, error });
      setError(error instanceof Error ? error.message : 'Failed to update session rebuild setting');
      showToast(t('errors.failedToUpdateSessionRebuild'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateStoreToolPayload = async (value: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPut('/system-config', {
        activityLog: {
          storeToolPayload: value,
        },
      });

      if (data.success) {
        setStoreToolPayload(value);
        showToast(t('settings.systemConfigUpdated'));
        return true;
      } else {
        setError(data.error || 'Failed to update activity log setting');
        showToast(data.error || t('settings.storeToolPayloadUpdateFailed'));
        return false;
      }
    } catch (error) {
      console.error('Failed to update activity log setting', { value, error });
      setError(error instanceof Error ? error.message : 'Failed to update activity log setting');
      showToast(t('settings.storeToolPayloadUpdateFailed'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const exportMCPSettings = async (serverName?: string) => {
    setLoading(true);
    setError(null);
    try {
      return await apiGet(`/mcp-settings/export?serverName=${serverName ? serverName : ''}`);
    } catch (error) {
      console.error('Failed to export MCP settings', { serverName, error });
      const errorMessage = error instanceof Error ? error.message : 'Failed to export MCP settings';
      setError(errorMessage);
      showToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Bearer key management helpers
  const refreshBearerKeys = async () => {
    try {
      const data: ApiResponse<BearerKey[]> = await apiGet('/auth/keys');
      if (data.success && Array.isArray(data.data)) {
        setBearerKeys(data.data);
      }
    } catch (error) {
      console.error('Failed to refresh bearer keys', { error });
      showToast(t('errors.failedToFetchSettings'));
    }
  };

  const createBearerKey = async (
    payload: Pick<BearerKey, 'name'> & Partial<Omit<BearerKey, 'id' | 'name' | 'token'>>,
  ): Promise<BearerKey | null> => {
    try {
      const data: ApiResponse<BearerKey> = await apiPost('/auth/keys', payload as any);
      if (data.success && data.data) {
        await refreshBearerKeys();
        showToast(t('settings.systemConfigUpdated'));
        return data.data;
      }
      showToast(data.message || t('errors.failedToUpdateRoutingConfig'));
      return null;
    } catch (error) {
      console.error('Failed to create bearer key', { error });
      showToast(t('errors.failedToUpdateRoutingConfig'));
      return null;
    }
  };

  const updateBearerKey = async (
    id: string,
    updates: Partial<Omit<BearerKey, 'id'>>,
  ): Promise<BearerKey | null> => {
    try {
      const data: ApiResponse<BearerKey> = await apiPut(`/auth/keys/${id}`, updates as any);
      if (data.success && data.data) {
        await refreshBearerKeys();
        showToast(t('settings.systemConfigUpdated'));
        return data.data;
      }
      showToast(data.message || t('errors.failedToUpdateRoutingConfig'));
      return null;
    } catch (error) {
      console.error('Failed to update bearer key', { id, error });
      showToast(t('errors.failedToUpdateRoutingConfig'));
      return null;
    }
  };

  const deleteBearerKey = async (id: string): Promise<boolean> => {
    try {
      const data: ApiResponse = await apiDelete(`/auth/keys/${id}`);
      if (data.success) {
        await refreshBearerKeys();
        showToast(t('settings.systemConfigUpdated'));
        return true;
      }
      showToast(data.message || t('errors.failedToUpdateRoutingConfig'));
      return false;
    } catch (error) {
      console.error('Failed to delete bearer key', { id, error });
      showToast(t('errors.failedToUpdateRoutingConfig'));
      return false;
    }
  };

  // Fetch settings when the component mounts or refreshKey changes
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings, refreshKey]);

  // Watch for authentication status changes - refetch settings after login
  useEffect(() => {
    if (auth.isAuthenticated) {
      console.log('[SettingsContext] User authenticated, triggering settings refresh');
      // When user logs in, trigger a refresh to load settings
      triggerRefresh();
    }
  }, [auth.isAuthenticated, triggerRefresh]);

  useEffect(() => {
    if (routingConfig) {
      setTempRoutingConfig({
        bearerAuthKey: routingConfig.bearerAuthKey,
        bearerAuthHeaderName: routingConfig.bearerAuthHeaderName,
        jsonBodyLimit: routingConfig.jsonBodyLimit,
      });
    }
  }, [routingConfig]);

  const value: SettingsContextValue = {
    routingConfig,
    tempRoutingConfig,
    setTempRoutingConfig,
    installConfig,
    smartRoutingConfig,
    toolResultCompressionConfig,
    mcpRouterConfig,
    modelscopeConfig,
    emailConfig,
    oauthServerConfig,
    betterAuthConfig,
    nameSeparator,
    enableSessionRebuild,
    storeToolPayload,
    bearerKeys,
    loading,
    error,
    setError,
    triggerRefresh,
    fetchSettings,
    updateRoutingConfig,
    updateInstallConfig,
    updateSmartRoutingConfig,
    updateSmartRoutingConfigBatch,
    updateToolResultCompressionConfig,
    updateToolResultCompressionConfigBatch,
    updateRoutingConfigBatch,
    updateModelscopeConfig,
    updateEmailConfigBatch,
    updateMCPRouterConfig,
    updateMCPRouterConfigBatch,
    updateOAuthServerConfig,
    updateOAuthServerConfigBatch,
    updateBetterAuthConfigBatch,
    updateNameSeparator,
    updateSessionRebuild,
    updateStoreToolPayload,
    exportMCPSettings,
    refreshBearerKeys,
    createBearerKey,
    updateBearerKey,
    deleteBearerKey,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};
